// Demo Mode Service - Automatically switches between real API and demo data
// Based on server availability

import {
  DEMO_MODE,
  demoUser,
  demoTeams,
  demoEmployees,
  demoWorkOrders,
  demoMachines,
  demoComments,
  demoStatistics,
  generateDemoTeamStatus,
  generateDemoNotification,
} from './demoData';

// Configuration
const SERVER_CHECK_INTERVAL = 30000; // Check every 30 seconds
const REQUEST_TIMEOUT = 5000; // 5 second timeout for server checks

let isServerAvailable = null; // null = unknown, true = available, false = unavailable
let lastServerCheck = 0;

/**
 * Check if server is available
 */
export const checkServerAvailability = async (apiUrl) => {
  const now = Date.now();
  
  // Don't check too frequently
  if (isServerAvailable !== null && (now - lastServerCheck) < SERVER_CHECK_INTERVAL) {
    return isServerAvailable;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    
    isServerAvailable = response.ok;
    lastServerCheck = now;
    
    if (isServerAvailable) {
      DEMO_MODE.enabled = false;
      console.log('✅ Server available - using real API');
    }
    
    return isServerAvailable;
  } catch (error) {
    isServerAvailable = false;
    lastServerCheck = now;
    DEMO_MODE.enabled = true;
    DEMO_MODE.timestamp = new Date().toISOString();
    console.log('⚠️ Server unavailable - switching to demo mode');
    return false;
  }
};

/**
 * Wrapper for API calls - automatically uses demo data if server unavailable
 */
export const withDemoFallback = async (apiCall, demoData, apiUrl) => {
  // Check server availability first
  const serverAvailable = await checkServerAvailability(apiUrl);
  
  if (!serverAvailable) {
    // Return demo data with a slight delay to simulate network request
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('📦 Returning demo data');
        resolve(demoData);
      }, 300);
    });
  }

  // Try real API call
  try {
    return await apiCall();
  } catch (error) {
    console.warn('API call failed, falling back to demo data:', error.message);
    DEMO_MODE.enabled = true;
    isServerAvailable = false;
    return demoData;
  }
};

/**
 * Demo Mode Service - Wraps all API calls
 */
export const DemoModeService = {
  // Auth
  login: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoUser, apiUrl);
  },

  // Work Orders
  getWorkOrders: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoWorkOrders, apiUrl);
  },

  getWorkOrder: async (realApiCall, apiUrl, id) => {
    const demoOrder = demoWorkOrders.find(wo => wo.id === id) || demoWorkOrders[0];
    return withDemoFallback(realApiCall, demoOrder, apiUrl);
  },

  createWorkOrder: async (realApiCall, apiUrl, data) => {
    const newOrder = {
      ...data,
      id: Math.floor(Math.random() * 10000),
      created_at: new Date().toISOString(),
      created_by: demoUser.id,
    };
    return withDemoFallback(realApiCall, newOrder, apiUrl);
  },

  updateWorkOrder: async (realApiCall, apiUrl, id, data) => {
    const updated = { ...data, id, updated_at: new Date().toISOString() };
    return withDemoFallback(realApiCall, updated, apiUrl);
  },

  // Teams
  getTeams: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoTeams, apiUrl);
  },

  // Employees
  getEmployees: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoEmployees, apiUrl);
  },

  // Machines
  getMachines: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoMachines, apiUrl);
  },

  // Comments
  getComments: async (realApiCall, apiUrl, workOrderId) => {
    const orderComments = demoComments.filter(c => c.work_order_id === workOrderId);
    return withDemoFallback(realApiCall, orderComments, apiUrl);
  },

  addComment: async (realApiCall, apiUrl, data) => {
    const newComment = {
      ...data,
      id: Math.floor(Math.random() * 10000),
      created_at: new Date().toISOString(),
      user_name: demoUser.name,
    };
    return withDemoFallback(realApiCall, newComment, apiUrl);
  },

  // Statistics
  getStatistics: async (realApiCall, apiUrl) => {
    return withDemoFallback(realApiCall, demoStatistics, apiUrl);
  },

  // Team Status
  updateTeamStatus: async (realApiCall, apiUrl, workOrderId, teamId, status) => {
    const demoStatus = generateDemoTeamStatus(workOrderId, teamId);
    demoStatus.status = status;
    return withDemoFallback(realApiCall, demoStatus, apiUrl);
  },

  // Notifications
  getNotifications: async (realApiCall, apiUrl) => {
    const demoNotifications = [
      generateDemoNotification('Ny arbeidsordre AO-2024-001 tildelt'),
      generateDemoNotification('Arbeidsordre AO-2024-002 er ferdig'),
    ];
    return withDemoFallback(realApiCall, demoNotifications, apiUrl);
  },

  // Generic wrapper for any API call
  wrapApiCall: async (realApiCall, fallbackData, apiUrl) => {
    return withDemoFallback(realApiCall, fallbackData, apiUrl);
  },

  // Utility methods
  isDemoMode: () => DEMO_MODE.enabled,
  
  forceServerCheck: () => {
    lastServerCheck = 0;
    isServerAvailable = null;
  },
};

export default DemoModeService;
